import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AsyncPipe,
  DOCUMENT,
  NgClass,
  NgComponentOutlet,
  NgTemplateOutlet,
} from '@angular/common';
import { B2bBreadcrumbComponent } from '@app/new-ui-lib/b2b-breadcrumb/components/b2b-breadcrumb/b2b-breadcrumb.component';
import { B2bDynamicLoaderPipe } from '@app/b2b-dynamic-lib/pipes/b2b-dynamic-loader-pipe.pipe';
import { B2bNewTabsComponent } from '@app/new-ui-lib/b2b-tabs/components/b2b-new-tabs/b2b-new-tabs.component';
import { DetailPageCartComponent } from '../../components/detail-page-cart/detail-page-cart.component';
import {
  APIRequestBodyModel,
  ClientProductRatePlanModel,
  ClientProductRoomModel,
  ContainerItemModel,
  ContainerItemOption,
  ContainerModel,
  LayoutModel,
} from '@app/models/server/layout.model';
import { PAGE_TYPES } from '@app/constants/app-settings';
import { catchError, of, Subscription, switchMap, tap } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { EventService } from '@app/services/util-services/root-services/event-service';
import { HttpClientService } from '@app/services/util-services/root-services/http-client.service';
import { ToastService } from '@app/services/util-services/root-services/toast-service';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { AnalyticsEventService } from '@app/services/util-services/root-services/analytics-event-service';
import { SeoSetupService } from '@b2b/seo-util-lib';
import { ApiResponseBody } from '@app/models/client/new-request-body.model';
import { HttpErrorResponse } from '@angular/common/http';
import { DesktopDetailPageDataService } from '../../services/desktop-detail-page-data.service';
import { B2bShortstayFormComponent } from '@app/new-ui-lib/b2b-search/components/b2b-desktop/b2b-shortstay-form/b2b-shortstay-form.component';
import { B2bShortPropertyCardComponent } from '@app/new-ui-lib/b2b-cards/components/b2b-short-property-card/b2b-short-property-card.component';
import { B2bImageGalleryComponent } from '@app/new-ui-lib/b2b-gallery/components/b2b-image-gallery/b2b-image-gallery.component';
import { DesktopImgSizesConstant } from '../../constants/desktop-img-sizes.constant';
import { B2bPropertyRoomCardComponent } from '@app/new-ui-lib/b2b-cards/components/b2b-property-room-card/b2b-property-room-card.component';
import { ActivityInformationCardComponent } from '@app/new-ui-lib/b2b-cards/components/activity-information-card/activity-information-card.component';

@Component({
  selector: 'lib-desktop-web-property-detail',
  templateUrl: './desktop-web-property-detail.component.html',
  styleUrls: ['./desktop-web-property-detail.component.scss'],
  imports: [
    AsyncPipe,
    B2bBreadcrumbComponent,
    B2bDynamicLoaderPipe,
    B2bNewTabsComponent,
    DetailPageCartComponent,
    NgTemplateOutlet,
    NgClass,
    B2bShortstayFormComponent,
    NgComponentOutlet,
    B2bShortPropertyCardComponent,
    B2bImageGalleryComponent,
    B2bPropertyRoomCardComponent,
    ActivityInformationCardComponent,
  ],
  providers: [DesktopDetailPageDataService],
  standalone: true,
})
export class DesktopWebPropertyDetailComponent implements OnInit, OnDestroy {
  @Input() layoutData: LayoutModel;

  pageType: string = PAGE_TYPES.DETAIL_PAGE;
  pageTypes = PAGE_TYPES;
  newActionApiSubscription: Subscription;
  newActionApiEventSubscription: Subscription;
  searchQuerySubscription: Subscription;
  resetDetailApiSubscription: Subscription;
  createCheckoutDataApiSubscription: Subscription;
  errorContainers: ContainerModel[] = [];
  initSeoDataSubscription$: Subscription;
  searchQueryChangedOnDetailPageSubscription: Subscription;

  isServer: boolean = this.b2bDeviceUtilService?.isServer();
  loadOnClient: boolean;

  skeletonKeys: {
    headerLoaded: boolean;
    errorLoaded: boolean;
  } = {
    headerLoaded: false,
    errorLoaded: false,
  };

  diffImageSizes = DesktopImgSizesConstant;

  constructor(
    private route: ActivatedRoute,
    private eventService: EventService,
    private httpClientService: HttpClientService,
    private router: Router,
    private toastService: ToastService,
    private b2bDeviceUtilService: B2bDeviceUtilService,
    private analyticsEventService: AnalyticsEventService,
    private detailPageDataService: DesktopDetailPageDataService,
    @Inject(DOCUMENT) private readonly document: Document,
    private seoSetupService: SeoSetupService,
  ) {}

  ngOnInit() {
    this.loadOnClient = this.route?.snapshot?.data?.loadOnClient;
    this.newActionApiEventSubscription =
      this.eventService.newActionApiEvent.subscribe({
        next: (result: {
          selectedRatePlans?: ClientProductRatePlanModel[];
          operationType?: string;
          rewardData?: {
            coupon: string;
            enableRedeemCreditPoints: boolean;
            membershipBody: {
              isAdd: boolean;
              apiRequestBody: APIRequestBodyModel;
            };
          };
          createCheckoutData?: boolean;
          foodItem?: {
            selectedContainer: ContainerItemModel;
            isAdded: boolean;
          };
          resetData?: {
            nextAvailableDate?: ContainerItemOption;
            nextAvailableCheckinTime?: ContainerItemOption;
            nextAvailableHour?: ContainerItemOption;
          };
        }) => {
          if (result?.createCheckoutData) {
            this.createCheckoutData(result);
          } else if (result?.foodItem?.selectedContainer) {
            this.newActionCallApi(result);
          } else if (result?.resetData) {
            this.resetData(result?.resetData, 'MODIFIED');
          } else {
            this.newActionCallApi(result);
          }
        },
      });

    this.searchQuerySubscription = this.route?.queryParams.subscribe({
      next: (queryParams: Params) => {
        if (this.b2bDeviceUtilService?.isServer()) {
          if (!this.router.url?.includes(`/feature/detail`)) {
            this.resetDetailPage(queryParams);
          }
        } else {
          this.resetDetailPage(queryParams);
        }
      },
    });

    this.searchQueryChangedOnDetailPageSubscription =
      this.eventService.searchQueryChangedOnDetailPageEvent.subscribe({
        next: (result: { queryParams?: any; resetLayoutData: boolean }) => {
          this.resetDetailPage(result?.queryParams);
        },
      });
  }

  createCheckoutData(data: {
    selectedRatePlans?: ClientProductRatePlanModel[];
    rewardData?: {
      coupon: string;
      enableRedeemCreditPoints: boolean;
      membershipBody: {
        isAdd: boolean;
        apiRequestBody: APIRequestBodyModel;
      };
    };
    foodItem?: {
      selectedContainer: ContainerItemModel;
      isAdded: boolean;
    };
  }) {
    this.createCheckoutDataApiSubscription = this.httpClientService
      .post(
        'create-checkout-data',
        {
          requestData: data,
          cartItems: this.layoutData?.cartContainer,
          queryParams: this.layoutData?.queryParams,
        },
        {},
      )
      .subscribe({
        next: (result: ApiResponseBody) => {
          this.router.navigateByUrl(`${result?.routerConfig?.nextUrl}`).then();
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.toastService.showApiError(errorResponse);
        },
      });
  }

  newActionCallApi(data: {
    selectedRatePlans?: ClientProductRatePlanModel[];
    operationType?: string;
    rewardData?: {
      coupon: string;
      enableRedeemCreditPoints: boolean;
      membershipBody: {
        isAdd: boolean;
        apiRequestBody: APIRequestBodyModel;
      };
    };
  }) {
    this.newActionApiSubscription = this.httpClientService
      .post(
        'new-feature-cart',
        {
          requestData: data,
          cartItems: this.layoutData?.cartContainer,
          queryParams: this.layoutData?.queryParams,
        },
        {},
      )
      .subscribe({
        next: (result: {
          headerContainer?: ContainerModel[];
          footerContainer?: ContainerModel[];
          cartContainer?: ContainerModel[];
          roomDataContainer?: ClientProductRoomModel[];
        }) => {
          this.eventService.roomDataContainerUpdatedEvent.emit({
            roomDataContainer: result?.roomDataContainer,
          });
          this.layoutData.cartContainer = result?.cartContainer;
          this.analyticsEventService.fireCartContainerEvent.emit({
            eventName: 'update_cart_event',
            cartContainer: this.layoutData.cartContainer,
          });
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.toastService.showApiError(errorResponse);
        },
      });
  }

  resetDetailPage(queryParams: any, isRefreshEvent?: boolean): void {
    let tempQueryParams = JSON.parse(JSON.stringify(queryParams));
    this.skeletonKeys.errorLoaded = false;
    let urlPath: string = this.router.url.toLowerCase();
    if (urlPath.includes('?')) {
      urlPath = urlPath.substring(0, urlPath.indexOf('?'));
    }
    if (!tempQueryParams?.path) {
      if (!tempQueryParams) {
        tempQueryParams = {};
      }
      tempQueryParams.path = urlPath;
    }
    this.resetDetailApiSubscription = this.detailPageDataService
      .initNewFeatureDetailPage({
        queryParams: tempQueryParams,
        path: urlPath,
      })
      .pipe(
        switchMap((result: LayoutModel) => {
          if (this.b2bDeviceUtilService.isServer()) {
            this.seoSetupService.updateDeviceType(result);
            this.seoSetupService.updateSeoData(result);
          }
          return of(result);
        }),
        tap((result: LayoutModel) => {
          if (!this.b2bDeviceUtilService.isServer()) {
            this.seoSetupService.updateSeoData(result);
          }
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          if (errorResponse?.status === 404) {
            this.router.navigateByUrl('/page-not-found').then();
          }
          return of({});
        }),
      )
      .subscribe({
        next: (result: LayoutModel) => {
          this.layoutData = result;
          if (
            !this.layoutData?.mainContainer ||
            this.layoutData?.mainContainer?.length === 0
          ) {
            this.skeletonKeys.errorLoaded = true;
          }
        },
        error: (errorResponse: HttpErrorResponse) => {
          if (errorResponse?.error?.errorContainer?.length > 0) {
            this.errorContainers = errorResponse?.error?.errorContainer;
          } else {
            this.toastService.showApiError(errorResponse);
          }
          this.skeletonKeys.errorLoaded = true;
        },
      });
  }

  onChangeTab(container: ContainerModel) {
    this.document?.getElementById(container?.key)?.scrollIntoView({
      behavior: 'smooth',
    });
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  destroy() {
    if (this.newActionApiEventSubscription) {
      this.newActionApiEventSubscription?.unsubscribe();
    }
    if (this.newActionApiSubscription) {
      this.newActionApiSubscription.unsubscribe();
    }
    if (this.resetDetailApiSubscription) {
      this.resetDetailApiSubscription?.unsubscribe();
    }
    if (this.searchQuerySubscription) {
      this.searchQuerySubscription.unsubscribe();
    }
    if (this.createCheckoutDataApiSubscription) {
      this.createCheckoutDataApiSubscription.unsubscribe();
    }
    if (this.initSeoDataSubscription$) {
      this.initSeoDataSubscription$.unsubscribe();
    }
    if (this.searchQueryChangedOnDetailPageSubscription) {
      this.searchQueryChangedOnDetailPageSubscription.unsubscribe();
    }
  }

  private resetData(
    resetData: {
      nextAvailableDate?: ContainerItemOption;
      nextAvailableCheckinTime?: ContainerItemOption;
      nextAvailableHour?: ContainerItemOption;
    },
    queryType: string,
  ) {
    const queryParams: any = this.layoutData?.queryParams;
    if (resetData?.nextAvailableDate) {
      queryParams.cind = resetData?.nextAvailableDate?.date;
    }
    if (resetData?.nextAvailableCheckinTime) {
      queryParams.cint = resetData?.nextAvailableCheckinTime?.title;
    }
    if (resetData?.nextAvailableHour) {
      queryParams.h = resetData?.nextAvailableHour?.value;
    }
    queryParams.ic = 1;
    queryParams.ot = queryType;
    this.resetDetailPage(queryParams);
  }
}
